'use client';

import ProductCard from '@/components/ProductCard';
import { SliderArrow } from '@/components/SliderArrow';
import { Default_Image } from '@/constants';
import { useElementOnScreen } from '@/hooks/intersectionObserver';
import { IconArrowLeftDouble } from '@/icons/IconArrowLeftDouble';
import { IconArrowRightDouble } from '@/icons/IconArrowRightDouble';
import { IProductSlide } from '@/interfaces/product';
import { useEffect, useState } from 'react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { Swiper, SwiperSlide } from 'swiper/react';
import { AutoplayOptions } from 'swiper/types';

const AuthProductSlider = ({
  title = '',
  products,
  className = '',
  isCategoryProduct,
  slideClassName = 'sm:pr-1',
  config = { slidesPerView: 'auto', spaceBetween: 14 },
  showArrow,
  autoplay = false,
  navigation,
  sliderType,
  autoPlayTimer,
  setSwiper = (s: any) => {},
  dataTestId = 'auth-product-slider',
}: {
  title?: string;
  products: IProductSlide[];
  className?: string;
  slideClassName?: string;
  config?: any;
  isCategoryProduct?: boolean;
  showArrow?: boolean;
  autoplay?: boolean | AutoplayOptions;
  navigation?: boolean;
  sliderType?: string;
  autoPlayTimer?: number;
  setSwiper?: (s: any) => void;
  dataTestId?: string;
}) => {
  const [isMounted, setIsMounted] = useState<boolean>(false);

  const { containerRef, isVisible } = useElementOnScreen({
    root: null,
    rootMargin: '-100px',
    threshold: 0.0,
  });

  useEffect(() => {
    if (isVisible) {
      setIsMounted(true);
      containerRef?.current?.swiper?.autoplay.start();
    } else {
      containerRef?.current?.swiper?.autoplay.stop();
    }
  }, [isVisible, containerRef]);

  const swiperInternalComponent = (products?: IProductSlide[]) => {
    return (
      <Swiper
        {...config}
        key={isMounted.toString()}
        ref={containerRef}
        autoplay={autoPlayTimer ? { delay: autoPlayTimer - 1000 } : false}
        modules={[Navigation, Autoplay]}
        className="content-slider"
        wrapperClass="flex"
        loop
        onSwiper={(s) => {
          setSwiper && typeof setSwiper === 'function' && setSwiper(s);
        }}
        slidesPerView={1}
        breakpoints={{
          400: {
            slidesPerView: 2,
            autoplay: autoPlayTimer ? { delay: autoPlayTimer } : false,
          },
          640: {
            slidesPerView: 3,
            autoplay: autoPlayTimer ? { delay: autoPlayTimer } : false,
          },
          1024: {
            slidesPerView: 4,
            autoplay: autoPlayTimer ? { delay: autoPlayTimer } : false,
          },
          1400: {
            slidesPerView: 5,
            autoplay: autoPlayTimer ? { delay: autoPlayTimer } : false,
          },
          1700: {
            slidesPerView: 6,
            autoplay: autoPlayTimer ? { delay: autoPlayTimer } : false,
          },
        }}
        navigation={
          sliderType
            ? {
                prevEl: `.${sliderType}-swipe-prev`,
                nextEl: `.${sliderType}-swipe-next`,
              }
            : false
        }
        data-testid={`${dataTestId}-swiper`}
      >
        {products?.map((slide, index) => (
          <SwiperSlide key={slide.seName + index} style={{ width: 'fit-content' }} data-testid={`${dataTestId}-slide-${index}`}>
            <ProductCard
              key={slide.seName}
              className={slideClassName}
              name={slide?.name}
              url={`/product/${slide.seName}`}
              category={slide?.CategoryName}
              options={slide?.options}
              imageUrl={slide?.FeaturedImageUrl !== '' ? slide?.FeaturedImageUrl : Default_Image}
              imageAlt={`Slide ${index}`}
              oldPrice={slide?.productPrice?.OldPrice}
              price={slide?.productPrice?.Price}
              seName={slide?.seName}
              isCategoryProduct={isCategoryProduct}
              isCategoryVisible={false}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    );
  };

  return (
    <div className={`product-details-swiper ${className} relative w-full ${navigation ? 'px-10 lg:px-12' : ''}`} data-testid={dataTestId}>
      {title && (
        <h4 className="mb-5 font-bold uppercase" data-testid={`${dataTestId}-title`}>
          {title}
        </h4>
      )}
      {isMounted ? swiperInternalComponent(products) : swiperInternalComponent(products && products?.length > 6 ? products?.slice(0, 6) : products)}

      {sliderType && (
        <>
          <div
            className={`${sliderType}-swipe-prev swipe-prev absolute bottom-0 left-0 right-0 top-0 md:hidden`}
            data-testid={`${dataTestId}-prev-arrow`}
          >
            <SliderArrow className="left-0 top-1/2 -translate-y-1/2 !p-1" data-testid={`${dataTestId}-prev-arrow-button`}>
              <IconArrowLeftDouble className="h-8 w-8 text-gray-600" />
            </SliderArrow>
          </div>
          <div
            className={`${sliderType}-swipe-next swipe-next absolute bottom-0 left-0 right-0 top-0 md:hidden`}
            data-testid={`${dataTestId}-next-arrow`}
          >
            <SliderArrow className="right-0 top-1/2 -translate-y-1/2 !p-1" data-testid={`${dataTestId}-next-arrow-button`}>
              <IconArrowRightDouble className="h-8 w-8 text-gray-600" />
            </SliderArrow>
          </div>
        </>
      )}
    </div>
  );
};

export default AuthProductSlider;
